import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { bankAccountsApi } from 'src/Services/API';
import { BankAccountBalanceDto, CreateBankAccountBalanceDto } from 'src/Services/API/generated';
import { trackBankBalanceRecorded } from 'src/Services/Mixpanel/events';

type CreateBankAccountBalanceParams = {
  bankAccountId: string;
  createData: CreateBankAccountBalanceDto;
};

export const useCreateBankAccountBalance = (
  options?: UseMutationOptions<BankAccountBalanceDto, unknown, CreateBankAccountBalanceParams>
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<BankAccountBalanceDto, unknown, CreateBankAccountBalanceParams>(
    ['create-bank-account-balance', assetId],
    async ({ bankAccountId, createData }: CreateBankAccountBalanceParams) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await bankAccountsApi.bankAccountsControllerCreateBalance(
        bankAccountId,
        assetId,
        createData
      );
      return response.data;
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(['fetch-bank-accounts', assetId]);
        queryClient.invalidateQueries(['fetch-bank-account-balances', variables.bankAccountId]);
        trackBankBalanceRecorded({
          bank_account_id: variables.bankAccountId,
          amount_sign: data.amount > 0 ? 'positive' : data.amount < 0 ? 'negative' : 'zero',
        });
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};
